import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Bell, Truck, Loader2, ShieldAlert } from 'lucide-react';
import './esd.css';
import api from './api/client';
import { AuthProvider, useAuth, hasRole } from './contexts/AuthContext';
import { Toaster } from './components/EsdToaster';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ROLE_LABELS, formatDateTime } from './lib/format';
import Dashboard from './pages/Dashboard';
import Equipment from './pages/Equipment';
import Operators from './pages/Operators';
import EquipmentRequests from './pages/EquipmentRequests';
import Inspections from './pages/Inspections';
import EquipmentQueues from './pages/EquipmentQueues';
import Mobilizations from './pages/Mobilizations';
import MobilizationForm from './pages/MobilizationForm';
import Maintenance from './pages/Maintenance';
import Consumables from './pages/Consumables';
import Utilizations from './pages/Utilizations';
import Contracts from './pages/Contracts';
import Billings from './pages/Billings';
import Settings from './pages/Settings';
import Users from './pages/Users';
import Roles from './pages/Roles';
import ApprovalWorkflow from './pages/ApprovalWorkflow';

const ALL = ['super_admin', 'admin', 'supervisor', 'approver', 'requestor', 'operator'];
const MGR = ['super_admin', 'admin', 'supervisor', 'approver'];

const Guard = ({ roles, children }) => {
  const { user } = useAuth();
  if (roles && !hasRole(user, ...roles)) {
    return (
      <div className="p-8 text-center" data-testid="esd-access-denied">
        <h2 className="text-2xl font-bold text-slate-900">Access Denied</h2>
        <p className="text-slate-600 mt-2">You don't have permission to view this page.</p>
      </div>
    );
  }
  return children;
};

const NotificationBell = () => {
  const [notifs, setNotifs] = useState([]);
  const unread = notifs.filter((n) => !n.is_read).length;
  const load = useCallback(async () => {
    try { setNotifs((await api.get('/notifications')).data || []); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  const markAll = async () => { await api.post('/notifications/read-all'); load(); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 grid place-items-center rounded-lg hover:bg-white/10" data-testid="esd-notifications-btn">
          <Bell className="h-5 w-5 text-white" />
          {unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-amber-400 text-[10px] font-bold text-slate-900 grid place-items-center" data-testid="esd-notifications-count">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-sm">ESD Notifications</div>
          {unread > 0 && <button onClick={markAll} className="text-xs text-[hsl(217,91%,24%)] font-medium" data-testid="esd-notifications-read-all">Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto divide-y">
          {notifs.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No notifications</div>}
          {notifs.slice(0, 30).map((n) => (
            <div key={n.id} className={`px-4 py-3 text-sm ${n.is_read ? '' : 'bg-blue-50/60'}`}>
              <div className="font-medium text-slate-900">{n.title}</div>
              <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
              <div className="text-[10px] text-slate-400 mt-1">{formatDateTime(n.created_at)}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const EsdHeader = () => {
  const { user } = useAuth();
  return (
    <div className="brand-gradient rounded-xl px-5 py-3 flex items-center justify-between gap-3 mb-5" data-testid="esd-header">
      <div className="flex items-center gap-3 text-white min-w-0">
        <div className="h-9 w-9 rounded-lg bg-amber-400 grid place-items-center text-[hsl(217,91%,18%)] shrink-0">
          <Truck className="h-5 w-5" strokeWidth={2.4} />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-base leading-none">ESD</div>
          <div className="text-[11px] text-blue-200/80 leading-none mt-1 truncate">Equipment Solutions Dept. · Motorpool Management</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="hidden sm:inline-flex text-xs font-medium px-2.5 py-1 rounded-full bg-amber-400/20 text-amber-200 border border-amber-300/30" data-testid="esd-role-badge">{ROLE_LABELS[user?.role] || user?.role}</span>
        <NotificationBell />
      </div>
    </div>
  );
};

const EsdRoutes = () => {
  const { user, loading, error } = useAuth();
  if (loading) return <div className="p-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!user) {
    return (
      <div className="rounded-xl border border-dashed border-amber-300 bg-white p-12 text-center max-w-lg mx-auto mt-10" data-testid="esd-no-access">
        <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <div className="text-slate-800 font-semibold">No ESD access yet</div>
        <p className="text-sm text-slate-500 mt-2">{error}</p>
      </div>
    );
  }
  return (
    <>
      <EsdHeader />
      <Routes>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="equipment" element={<Equipment />} />
        <Route path="operators" element={<Guard roles={MGR}><Operators /></Guard>} />
        <Route path="equipment-requests" element={<EquipmentRequests />} />
        <Route path="inspections" element={<Guard roles={MGR}><Inspections /></Guard>} />
        <Route path="equipment-queues" element={<Guard roles={MGR}><EquipmentQueues /></Guard>} />
        <Route path="mobilizations" element={<Mobilizations />} />
        <Route path="mobilizations/:id/form" element={<MobilizationForm />} />
        <Route path="consumables" element={<Guard roles={ALL}><Consumables /></Guard>} />
        <Route path="maintenance" element={<Guard roles={['super_admin', 'admin', 'supervisor']}><Maintenance /></Guard>} />
        <Route path="utilizations" element={<Guard roles={['super_admin', 'admin', 'supervisor']}><Utilizations /></Guard>} />
        <Route path="contracts" element={<Guard roles={['super_admin', 'admin']}><Contracts /></Guard>} />
        <Route path="billings" element={<Guard roles={['super_admin', 'admin']}><Billings /></Guard>} />
        <Route path="settings" element={<Guard roles={['super_admin', 'admin']}><Settings /></Guard>}>
          <Route path="users" element={<Users />} />
          <Route path="roles" element={<Guard roles={['super_admin']}><Roles /></Guard>} />
          <Route path="approvals" element={<Guard roles={['super_admin']}><ApprovalWorkflow /></Guard>} />
          <Route path="*" element={<Navigate to="/esd/settings" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/esd/dashboard" replace />} />
      </Routes>
    </>
  );
};

export default function EsdApp() {
  return (
    <div className="esd-app" data-testid="esd-app">
      <AuthProvider>
        <EsdRoutes />
        <Toaster />
      </AuthProvider>
    </div>
  );
}
